import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../../CartContext'; 
import './ProductDetail.css';
import ProductService from './Productservice';
import '@fortawesome/fontawesome-free/css/all.min.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cartCount } = useCart();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addedToCart, setAddedToCart] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('description');

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const foundProduct = await ProductService.getProductById(id);
        
        if (foundProduct) {
          const mappedProduct = ProductService.mapProductToFrontend(foundProduct);
          setProduct(mappedProduct);
        } else {
          setError('Produit non trouvé');
        }
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Erreur lors du chargement du produit');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const parseTechnicalSpecs = (specsText) => {
    if (!specsText) return [];
    
    const specs = specsText.split('|').map(spec => {
      const [label, ...valueParts] = spec.split(':');
      const value = valueParts.join(':').trim();
      return {
        label: label.trim(),
        value: value
      };
    }).filter(spec => spec.label && spec.value);
    
    return specs;
  };

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  const handleGoBack = () => navigate(-1);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  if (loading) {
    return (
      <div className="product-detail-container">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement du produit...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-container">
        <div className="error-state">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>{error || 'Produit introuvable'}</h2>
          <button className="btn-back-home" onClick={handleGoBack}>
            <i className="fas fa-arrow-left"></i> Retour
          </button>
        </div>
      </div>
    );
  }

  const productImages = [
    product.adresse_Image_Product || 'https://via.placeholder.com/600x600?text=Produit',
    product.adresse_Image_Product || 'https://via.placeholder.com/600x600?text=Produit+2',
    product.adresse_Image_Product || 'https://via.placeholder.com/600x600?text=Produit+3',
  ];

  const technicalSpecs = product.technicalSpecificationsText 
    ? parseTechnicalSpecs(product.technicalSpecificationsText)
    : product.technicalSpecifications || [];

  return (
    <div className="product-detail-container">

      <div className="products-topbar">
        <div className="topbar-cart" onClick={() => navigate('/cart')}>
          <i className="fas fa-shopping-cart"></i>
          {cartCount > 0 && (
            <span className="topbar-badge">{cartCount}</span>
          )}
        </div>
      </div>

      <div className="product-detail-wrapper">

        <div className="detail-breadcrumb">
          <button className="breadcrumb-back" onClick={handleGoBack}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="breadcrumb-path">
            <span>TSE</span>
            <i className="fas fa-chevron-right"></i>
            <span>{product.category || 'Catégorie'}</span>
            <i className="fas fa-chevron-right"></i>
            <span>{product.subCategory || 'Sous-catégorie'}</span>
            <i className="fas fa-chevron-right"></i>
            <span className="breadcrumb-current">{product.name}</span>
          </div>
        </div>

        <div className="product-detail-content">

          <div className="product-images-section">
            <div className="main-image-container">
              <img 
                src={productImages[selectedImage]} 
                alt={product.name}
                className="main-product-image"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/600x600?text=Image+non+disponible';
                }}
              />
              <button className="zoom-btn" title="Zoom">
                <i className="fas fa-search-plus"></i>
              </button>
            </div>
            
            <div className="thumbnail-gallery">
              {productImages.map((img, index) => (
                <div 
                  key={index}
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img 
                    src={img} 
                    alt={`Vue ${index + 1}`}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/150x150?text=Image';
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="product-info-section">
            <h1 className="product-title">{product.name}</h1>
            
            {(product.reference || product.barcode) && (
              <div className="product-reference">
                <span className="ref-label">Réf :</span>
                <span className="ref-value">{product.reference || product.barcode}</span>
              </div>
            )}

            {(product.brand || product.family) && (
              <div className="product-brand">
                <span className="brand-label">Marque :</span>
                <span className="brand-value">{product.brand || product.family}</span>
              </div>
            )}

            <div className="product-price-section">
              <div className="price-main">
                <span className="price-amount">
                  {product.price.toLocaleString('fr-TN', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })} DT
                </span>
                <span className="price-tax">TTC</span>
              </div>
              {product.installment && (
                <div className="installment-info">
                  <i className="fas fa-info-circle"></i>
                  <span>Paiement en 3x sans frais disponible</span>
                </div>
              )}
            </div>

            {product.description && (
              <div className="product-description">
                <p>{product.description}</p>
              </div>
            )}

            <div className="product-features">
              {product.freeDelivery && (
                <div className="feature-badge">
                  <i className="fas fa-shipping-fast"></i>
                  <span>Livraison gratuite</span>
                </div>
              )}
              {product.inStock && (
                <div className="feature-badge stock">
                  <i className="fas fa-check-circle"></i>
                  <span>En stock</span>
                </div>
              )}
              {product.installment && (
                <div className="feature-badge payment">
                  <i className="fas fa-credit-card"></i>
                  <span>Paiement en plusieurs fois</span>
                </div>
              )}
            </div>

            <div className="purchase-section">
              <div className="quantity-selector">
                <label>Quantité</label>
                <div className="quantity-controls">
                  <button 
                    className="qty-btn" 
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                  >
                    <i className="fas fa-minus"></i>
                  </button>
                  <input 
                    type="number" 
                    value={quantity} 
                    readOnly
                    className="qty-input"
                  />
                  <button 
                    className="qty-btn" 
                    onClick={() => handleQuantityChange(1)}
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
              </div>

              <button 
                className={`btn-add-to-cart ${addedToCart ? 'added' : ''}`}
                onClick={handleAddToCart}
              >
                <i className={`fas ${addedToCart ? 'fa-check' : 'fa-shopping-cart'}`}></i>
                {addedToCart ? 'Ajouté !' : "j'achète"}
              </button>
            </div>

            {addedToCart && (
              <div className="cart-success-message">
                <i className="fas fa-check-circle"></i>
                <span>{quantity} x {product.name} ajouté au panier !</span>
              </div>
            )}

          </div>
        </div>

        <div className="product-additional-info">
          <div className="info-tabs">
            <button 
              className={`info-tab ${activeTab === 'description' ? 'active' : ''}`}
              onClick={() => setActiveTab('description')}
            >
              <i className="fas fa-info-circle"></i>
              Description
            </button>
            <button 
              className={`info-tab ${activeTab === 'specs' ? 'active' : ''}`}
              onClick={() => setActiveTab('specs')}
            >
              <i className="fas fa-cog"></i>
              Caractéristiques
            </button>
            <button 
              className={`info-tab ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              <i className="fas fa-star"></i>
              Avis clients
            </button>
          </div>
          
          <div className="info-content">
            
            {activeTab === 'description' && (
              <>
                <h3>Description détaillée</h3>
                
                
                

                {technicalSpecs.length > 0 && (
                  <>
                    <table className="technical-specs-table">
                      <tbody>
                        {technicalSpecs.map((spec, index) => (
                          <tr key={index}>
                            <td className="tech-spec-label-col">{spec.label}</td>
                            <td className="tech-spec-value-col">{spec.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </>
            )}

            {activeTab === 'specs' && (
              <div className="product-specs">
                <h4>Caractéristiques techniques</h4>
                
                {technicalSpecs.length > 0 ? (
                  <table className="specs-table">
                    <tbody>
                      {technicalSpecs.map((spec, index) => (
                        <tr key={index} className="spec-row">
                          <td className="spec-label">
                            <i className="fas fa-caret-right"></i>
                            {spec.label}
                          </td>
                          <td className="spec-value">{spec.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>Aucune caractéristique technique disponible pour ce produit.</p>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="product-reviews">
                <h4>Avis clients</h4>
                <p>Aucun avis client pour le moment.</p>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

export default ProductDetail;